import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { useCallback, useMemo } from 'react';

/**
 * Zustand store for tile selection (memory-only, no localStorage).
 * Keyed by fieldType (string) so multiple fields on the same page don't interfere.
 *
 * IMPORTANT: selected[fieldType] is stored as Record<string, true> (object-as-set),
 * NOT as an array. This gives O(1) per-tile lookup instead of O(n) Array.includes,
 * which matters because 1000+ tiles each subscribe with a selector that checks their
 * individual state on every store update.
 */
export const useSelectionStore = create(
	immer( ( set ) => ( {
			// { [fieldType: string]: Record<tileId, true> }
			selected: {},

			toggle( fieldType, tileId, allTileIdsInBundle ) {
				set( ( state ) => {
					if ( ! state.selected[ fieldType ] ) state.selected[ fieldType ] = {};
					const sel        = state.selected[ fieldType ];
					const ids        = allTileIdsInBundle ?? [ tileId ];
					const isSelected = ids.every( ( id ) => !! sel[ id ] );

					if ( isSelected ) {
						for ( const id of ids ) delete sel[ id ];
					} else {
						for ( const id of ids ) sel[ id ] = true;
					}
				} );
			},

			selectMany( fieldType, tileIds ) {
				set( ( state ) => {
					if ( ! state.selected[ fieldType ] ) state.selected[ fieldType ] = {};
					for ( const id of tileIds ) state.selected[ fieldType ][ id ] = true;
				} );
			},

			deselectMany( fieldType, tileIds ) {
				set( ( state ) => {
					if ( ! state.selected[ fieldType ] ) return;
					for ( const id of tileIds ) delete state.selected[ fieldType ][ id ];
				} );
			},

			clear( fieldType ) {
				set( ( state ) => { state.selected[ fieldType ] = {}; } );
			},
		} ) )
);

// Stable references to actions — grabbed once outside React so action callbacks
// never change identity and don't cause unnecessary re-renders.
const _toggle     = useSelectionStore.getState().toggle;
const _clear      = useSelectionStore.getState().clear;

// ─── Hook for PitchField root ─────────────────────────────────────────────────

/**
 * @param {string} fieldType
 * @param {Array}  tiles   - Full tile list from buildGrid (used to build bundle map)
 * @returns {{ toggleTile: Function, clearSelection: Function }}
 */
export function useSelectionActions( fieldType, tiles ) {
	const bundleTilesMap = useMemo( () => {
		const byBundle = {};
		for ( const tile of tiles ) {
			if ( tile.bundleId ) {
				if ( ! byBundle[ tile.bundleId ] ) byBundle[ tile.bundleId ] = [];
				byBundle[ tile.bundleId ].push( tile.id );
			}
		}
		const map = {};
		for ( const ids of Object.values( byBundle ) ) {
			for ( const id of ids ) map[ id ] = ids;
		}
		return map;
	}, [ tiles ] );

	const toggleTile = useCallback(
		( tileId ) => _toggle( fieldType, tileId, bundleTilesMap[ tileId ] ?? null ),
		[ fieldType, bundleTilesMap ]
	);

	const clearSelection = useCallback(
		() => _clear( fieldType ),
		[ fieldType ]
	);

	return { toggleTile, clearSelection };
}
